import type { User } from '../../types';
import { supabase } from '../../services/supabaseService';

const CLAIM_MINUTES = 25;

export type EngagementClaim = {
  claimed_by_user_id: string;
  claimed_by_name: string;
  claimed_until: string;
};

/** Merge-safe parse: ai_parsed_data may be object or JSON string from older rows */
function normalizeAiParsed(raw: unknown): Record<string, unknown> {
  if (!raw) return {};
  if (typeof raw === 'string') {
    try {
      const p = JSON.parse(raw);
      return typeof p === 'object' && p !== null ? { ...(p as object) } : {};
    } catch {
      return {};
    }
  }
  if (typeof raw === 'object') return { ...(raw as object) };
  return {};
}

export async function fetchEngagementClaim(candidateId: string): Promise<EngagementClaim | null> {
  if (!candidateId) return null;
  try {
    const { data, error } = await supabase.from('candidates').select('ai_parsed_data').eq('id', candidateId).maybeSingle();
    if (error || !data) return null;
    const ai = normalizeAiParsed(data.ai_parsed_data);
    const cl = ai.engagement_claim as EngagementClaim | undefined;
    if (!cl?.claimed_until) return null;
    if (new Date(cl.claimed_until) <= new Date()) return null;
    return cl;
  } catch {
    return null;
  }
}

export type AcquireEngagementClaimResult =
  | { ok: true; claim: EngagementClaim }
  | { ok: false; claim: EngagementClaim };

/** Zachte lock: andere recruiters zien dat jij contact hebt “geclaimd”. Faalt open bij DB-fout. */
export async function tryAcquireEngagementClaim(
  candidateId: string,
  user: User
): Promise<AcquireEngagementClaimResult> {
  const until = new Date(Date.now() + CLAIM_MINUTES * 60 * 1000).toISOString();
  const newClaim: EngagementClaim = {
    claimed_by_user_id: user.id,
    claimed_by_name: user.name || 'Recruiter',
    claimed_until: until,
  };
  if (!candidateId) return { ok: true, claim: newClaim };
  try {
    const { data: row, error } = await supabase.from('candidates').select('ai_parsed_data').eq('id', candidateId).maybeSingle();
    if (error) return { ok: true, claim: newClaim };
    const d = normalizeAiParsed(row?.ai_parsed_data);
    const existing = d.engagement_claim as EngagementClaim | undefined;
    if (existing?.claimed_until && new Date(existing.claimed_until) > new Date()) {
      if (existing.claimed_by_user_id !== user.id) {
        return { ok: false, claim: existing };
      }
    }
    d.engagement_claim = newClaim;
    const { error: upErr } = await supabase.from('candidates').update({ ai_parsed_data: d }).eq('id', candidateId);
    // UI moet altijd een claim tonen; bij DB-fout houden we lokale claim (retry kan later)
    if (upErr) return { ok: true, claim: newClaim };
    return { ok: true, claim: newClaim };
  } catch {
    return { ok: true, claim: newClaim };
  }
}

export async function releaseEngagementClaim(candidateId: string, userId: string): Promise<void> {
  if (!candidateId || !userId) return;
  try {
    const { data: row } = await supabase.from('candidates').select('ai_parsed_data').eq('id', candidateId).maybeSingle();
    const d = normalizeAiParsed(row?.ai_parsed_data);
    const ex = d.engagement_claim as EngagementClaim | undefined;
    if (ex?.claimed_by_user_id === userId) {
      delete d.engagement_claim;
      await supabase.from('candidates').update({ ai_parsed_data: d }).eq('id', candidateId);
    }
  } catch {
    /* ignore */
  }
}
